import React, { useState, useEffect, useRef } from 'react';
import './contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.contact__info-item, .contact__form-group');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { name, email, subject, message } = formData;
    const mailtoLink = `mailto:itpromastery@gmail.com?subject=${encodeURIComponent(subject)}&body=Name: ${encodeURIComponent(name)}%0D%0AEmail: ${encodeURIComponent(email)}%0D%0ASubject: ${encodeURIComponent(subject)}%0D%0AMessage: ${encodeURIComponent(message)}`;
    
    setTimeout(() => {
      window.location.href = mailtoLink;
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: '📧',
      title: 'Email',
      detail: 'itpromastery@gmail.com',
      link: 'mailto:itpromastery@gmail.com'
    },
    {
      icon: '📍',
      title: 'Location',
      detail: 'Remote / Worldwide',
      link: null
    },
    {
      icon: '💬',
      title: 'Telegram',
      detail: '@ITProMastery',
      link: 'https://t.me/itpromastery'
    }
  ];

  return (
    <section className="contact" id="contact" ref={sectionRef}>
      <div className="contact__container">
        {/* Header */}
        <div className="contact__header">
          <span className="contact__badge">Contact</span>
          <h2 className="contact__title">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <div className="contact__underline"></div>
          <p className="contact__subtitle">
            Have a question or want to work together? Let's connect!
          </p>
        </div>

        {/* Content */}
        <div className="contact__content">
          {/* Left Side - Info */}
          <div className="contact__info">
            <div className="contact__info-card">
              <h3 className="contact__info-title">Let's talk about everything</h3>
              <p className="contact__info-description">
                Don't like forms? Send me an email or reach out through my social channels. 👋
              </p>
            </div>

            <div className="contact__info-items">
              {contactInfo.map((item, index) => (
                <div 
                  className="contact__info-item" 
                  key={index}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="contact__info-icon">
                    <span>{item.icon}</span>
                  </div>
                  <div className="contact__info-detail">
                    <span className="contact__info-label">{item.title}</span>
                    {item.link ? (
                      <a href={item.link} className="contact__info-value" target="_blank" rel="noopener noreferrer">
                        {item.detail}
                      </a>
                    ) : (
                      <span className="contact__info-value">{item.detail}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="contact__social">
              <span className="contact__social-label">Connect with me</span>
              <div className="contact__social-links">
                <a href="#" className="social-link github">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.15 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.62.24 2.85.12 3.15.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                  </svg>
                </a>
                <a href="#" className="social-link youtube">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="#" className="social-link linkedin">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" className="social-link twitter">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.104c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 0021.755-5.966c.395-1.185.59-2.437.59-3.695 0-.253-.013-.505-.04-.756A9.973 9.973 0 0024 4.57z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <form className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__form-header">
              <span className="form-emoji">✉️</span>
              <h3 className="contact__form-title">Send me a message</h3>
            </div>

            <div className="contact__form-row">
              <div className="contact__form-group">
                <label className="contact__form-label">Full Name</label>
                <input 
                  type="text" 
                  className="contact__form-input" 
                  placeholder="John Doe" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="contact__form-group">
                <label className="contact__form-label">Email Address</label>
                <input 
                  type="email" 
                  className="contact__form-input" 
                  placeholder="john@example.com" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="contact__form-group">
              <label className="contact__form-label">Subject</label>
              <input 
                type="text" 
                className="contact__form-input" 
                placeholder="Project Discussion" 
                name="subject" 
                value={formData.subject} 
                onChange={handleChange}
                required
              />
            </div>

            <div className="contact__form-group">
              <label className="contact__form-label">Message</label>
              <textarea 
                name="message" 
                className="contact__form-textarea"
                placeholder="Tell me about your project..."
                value={formData.message} 
                onChange={handleChange}
                required
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="contact__submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="contact__success">
                <span className="success-icon">✓</span>
                <p>Message sent successfully! I'll get back to you soon.</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;